import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield } from "lucide-react";

export const LegalMentions = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-10 lg:inset-20 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Mentions Légales</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Fermer"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10">
              <div className="max-w-4xl mx-auto prose prose-blue">
                
                {/* Éditeur */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    1. Éditeur du site
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Le site <strong>potentielplus.net</strong> est édité par :
                  </p>
                  <div className="bg-blue-50 rounded-xl p-5 mt-4">
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Raison sociale :</strong> POTENTIEL+</li>
                      <li><strong>Forme juridique :</strong> SASU (Société par Actions Simplifiée Unipersonnelle)</li>
                      <li><strong>Siège social :</strong> 23 rue de Goupillières, 78910 FLEXANVILLE, France</li>
                      <li><strong>SIRET :</strong> 918 294 844 00017</li>
                      <li><strong>TVA :</strong> Non assujetti à la TVA</li>
                      <li><strong>Téléphone :</strong> 06 64 44 68 23</li>
                      <li><strong>Email :</strong> contact@potentielplus.net</li>
                    </ul>
                  </div>
                </section>

                {/* Directeur de publication */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    2. Directeur de la publication
                  </h3>
                  <p className="text-gray-700">
                    Le directeur de la publication est <strong>Monsieur Kevin PIERRE</strong>, 
                    en sa qualité de représentant légal de la société POTENTIEL+.
                  </p>
                </section>

                {/* Hébergement */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    3. Hébergement
                  </h3>
                  <p className="text-gray-700">
                    Le site est hébergé par :
                  </p>
                  <div className="bg-gray-50 rounded-xl p-5 mt-4">
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Emergent Labs Inc.</strong></li>
                      <li>San Francisco, CA, États-Unis</li>
                      <li>Site web : emergentagent.com</li>
                    </ul>
                  </div>
                </section>

                {/* Propriété intellectuelle */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    4. Propriété intellectuelle
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, logiciels, 
                    base de données, etc.) est protégé par le droit d'auteur et le droit des marques. 
                    Toute reproduction, représentation, modification, publication, adaptation de tout ou partie 
                    des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, 
                    sauf autorisation écrite préalable de POTENTIEL+.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Le logo et la marque POTENTIEL+ sont des marques déposées. Toute utilisation non autorisée 
                    de ces marques est strictement interdite.
                  </p>
                </section>

                {/* Protection des données */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    5. Protection des données personnelles (RGPD)
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi 
                    Informatique et Libertés du 6 janvier 1978 modifiée, vous disposez des droits suivants 
                    concernant vos données personnelles :
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                    <li>Droit d'accès à vos données</li>
                    <li>Droit de rectification</li>
                    <li>Droit à l'effacement (droit à l'oubli)</li>
                    <li>Droit à la limitation du traitement</li>
                    <li>Droit à la portabilité des données</li>
                    <li>Droit d'opposition</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Pour exercer ces droits ou pour toute question relative à vos données personnelles, 
                    vous pouvez nous contacter à : <strong>contact@potentielplus.net</strong>
                  </p>
                  
                  <h4 className="text-lg font-semibold text-blue-800 mt-6 mb-3">Données collectées</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Les données personnelles collectées via le formulaire de contact (nom, email, téléphone, message) 
                    sont utilisées uniquement pour répondre à vos demandes et vous fournir des informations 
                    sur nos services de soutien scolaire. Ces données ne sont jamais vendues ni transmises 
                    à des tiers à des fins commerciales.
                  </p>
                  
                  <h4 className="text-lg font-semibold text-blue-800 mt-6 mb-3">Durée de conservation</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Vos données sont conservées pendant une durée maximale de 3 ans à compter du dernier 
                    contact, conformément à la réglementation en vigueur.
                  </p>
                </section>

                {/* Cookies */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    6. Cookies
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Ce site utilise des cookies techniques nécessaires à son bon fonctionnement. 
                    Ces cookies ne collectent aucune donnée personnelle à des fins publicitaires.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Vous pouvez configurer votre navigateur pour refuser les cookies. Toutefois, 
                    cela pourrait affecter le bon fonctionnement de certaines fonctionnalités du site.
                  </p>
                </section>

                {/* Responsabilité */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    7. Limitation de responsabilité
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    POTENTIEL+ s'efforce d'assurer l'exactitude et la mise à jour des informations 
                    diffusées sur ce site. Toutefois, POTENTIEL+ ne peut garantir l'exactitude, 
                    la précision ou l'exhaustivité des informations mises à disposition sur ce site.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    POTENTIEL+ décline toute responsabilité pour toute imprécision, inexactitude 
                    ou omission portant sur des informations disponibles sur ce site.
                  </p>
                </section>

                {/* Liens externes */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    8. Liens externes
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Ce site peut contenir des liens vers d'autres sites web. POTENTIEL+ n'exerce aucun 
                    contrôle sur ces sites et décline toute responsabilité quant à leur contenu ou aux 
                    dommages qui pourraient résulter de leur utilisation.
                  </p>
                </section>

                {/* Droit applicable */}
                <section className="mb-10">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    9. Droit applicable et juridiction
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Les présentes mentions légales sont soumises au droit français. En cas de litige, 
                    et après échec de toute tentative de recherche d'une solution amiable, les tribunaux 
                    français seront seuls compétents pour connaître de ce litige.
                  </p>
                </section>

                {/* Contact */}
                <section className="mb-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 pb-2 border-b-2 border-orange-400">
                    10. Contact
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Pour toute question concernant ces mentions légales, vous pouvez nous contacter :
                  </p>
                  <div className="bg-orange-50 rounded-xl p-5 mt-4">
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Email :</strong> contact@potentielplus.net</li>
                      <li><strong>Téléphone :</strong> 06 64 44 68 23</li>
                      <li><strong>Adresse :</strong> 23 rue de Goupillières, 78910 FLEXANVILLE</li>
                    </ul>
                  </div>
                </section>

                {/* Date de mise à jour */}
                <p className="text-sm text-gray-500 text-center mt-10 pt-6 border-t border-gray-200">
                  Dernière mise à jour : Avril 2026
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Hook to manage legal mentions modal
export const useLegalMentions = () => {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  return { isOpen, open, close };
};
